import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { investorsAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

const investorFormSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  document: z.string().min(1, 'Documento é obrigatório'),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  role: z.enum(['INVESTOR', 'ADMINISTRATOR']),
})

type InvestorFormValues = z.infer<typeof investorFormSchema>

interface InvestorFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  investor?: any
}

export function InvestorFormDialog({
  open,
  onOpenChange,
  investor,
}: InvestorFormDialogProps) {
  const queryClient = useQueryClient()
  const isEdit = !!investor

  const form = useForm<InvestorFormValues>({
    resolver: zodResolver(investorFormSchema),
    defaultValues: investor
      ? {
          name: investor.name,
          document: investor.document,
          email: investor.email || '',
          phone: investor.phone || '',
          address: investor.address || '',
          role: investor.role || 'INVESTOR',
        }
      : {
          name: '',
          document: '',
          email: '',
          phone: '',
          address: '',
          role: 'INVESTOR',
        },
  })

  const mutation = useMutation({
    mutationFn: async (values: InvestorFormValues) => {
      const payload: any = {
        name: values.name,
        document: values.document.replace(/\D/g, ''),
        email: values.email || undefined,
        phone: values.phone || undefined,
        address: values.address || undefined,
        role: values.role,
      }

      if (isEdit) {
        return investorsAPI.update(investor.id, payload)
      }
      return investorsAPI.create(payload)
    },
    onSuccess: () => {
      toast.success(
        isEdit ? 'Investidor atualizado!' : 'Investidor cadastrado!'
      )
      queryClient.invalidateQueries({ queryKey: ['investors'] })
      if (isEdit) {
        queryClient.invalidateQueries({
          queryKey: ['investor', investor.id],
        })
      }
      onOpenChange(false)
      form.reset()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = (values: InvestorFormValues) => {
    mutation.mutate(values)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Investidor' : 'Novo Investidor'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize as informações do investidor.'
              : 'Preencha as informações para cadastrar um novo investidor.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">
              Informações Pessoais
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  {...form.register('name')}
                  placeholder="Nome do investidor"
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="document">Documento (CPF/CNPJ) *</Label>
                <Input
                  id="document"
                  {...form.register('document')}
                  placeholder="000.000.000-00 ou 00.000.000/0001-00"
                />
                {form.formState.errors.document && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.document.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  {...form.register('email')}
                  placeholder="email@exemplo.com"
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  {...form.register('phone')}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address">Endereço</Label>
              <Input
                id="address"
                {...form.register('address')}
                placeholder="Endereço completo"
              />
            </div>

            <div>
              <Label>Tipo de Sócio *</Label>
              <Select
                value={form.watch('role')}
                onValueChange={(value) =>
                  form.setValue('role', value as 'INVESTOR' | 'ADMINISTRATOR')
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INVESTOR">Sócio Investidor</SelectItem>
                  <SelectItem value="ADMINISTRATOR">Sócio Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEdit ? 'Salvar' : 'Cadastrar Investidor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
